import { Box, Container, Typography, Grid, useTheme, useMediaQuery } from '@mui/material';
import { Person, CalendarMonth, Refresh, Notifications, Link } from '@mui/icons-material';
import heromob from '../../assets/slide0.png';
import bg1 from '../../assets/bg1.png';

const AppFeaturesSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  const features = [
    {
      icon: <Person sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: 'white' }} />,
      title: '24/7 PERSONALIZED SUPPORT',
      description: 'Instant help from real care experts anytime.',
    },
    {
      icon: <CalendarMonth sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: 'white' }} />,
      title: 'COMPREHENSIVE SERVICE ACCESS',
      description: 'All care services in one easy platform.',
    },
    {
      icon: <Refresh sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: 'white' }} />,
      title: 'REAL-TIME UPDATES',
      description: 'Live updates on care plans and routines.',
    },
    {
      icon: <Notifications sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: 'white' }} />,
      title: 'USER-FRIENDLY DESIGN',
      description: 'Simple and accessible for all users.',
    },
    {
      icon: <Link sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: 'white' }} />,
      title: 'COMMUNITY CONNECTION',
      description: 'Access resources, events, and support.',
    },
  ];

  return (
    <Box
      sx={{
        backgroundImage: `url(${bg1})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        py: { xs: 4, sm: 6, md: 8 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(101, 52, 183, 0.6)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box textAlign="center" mb={{ xs: 4, sm: 6, md: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem', lg: '3.2rem' },
              mb: { xs: 2, sm: 3 },
              letterSpacing: '0.1em',
            }}
          >
            APP FEATURES
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              maxWidth: { xs: '90%', sm: '80%', md: '600px' },
              mx: 'auto',
              lineHeight: 1.6,
              px: { xs: 2, sm: 0 },
            }}
          >
            Transforming care delivery for the Hesley Group—our app brings personalized support,
            secure communication, and seamless access to services right to your fingertips.
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            minHeight: { xs: 'auto', sm: '600px', md: '700px', lg: '800px' },
          }}
        >
          {isMobile ? (
            <Box>
              <Grid container spacing={3} justifyContent="center">
                {features.slice(0, 2).map((feature, i) => (
                  <Grid item xs={12} key={i}>
                    <Box textAlign="center" color="white" px={2}>
                      <Box mb={1.5}>{feature.icon}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <Box
                  component="img"
                  src={heromob}
                  alt="Mobile App Interface"
                  sx={{
                    width: '100%',
                    maxWidth: 320,
                    height: 'auto',
                  }}
                />
              </Box>

              <Grid container spacing={3} justifyContent="center">
                {features.slice(2).map((feature, i) => (
                  <Grid item xs={12} key={i + 2}>
                    <Box textAlign="center" color="white" px={2}>
                      <Box mb={1.5}>{feature.icon}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : isTablet ? (
            <Box>
              <Grid container spacing={4} justifyContent="center" mb={4}>
                {features.slice(0, 2).map((feature, i) => (
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    key={i}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <Box textAlign="center" color="white" maxWidth={280}>
                      <Box mb={2}>{feature.icon}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                <Box
                  component="img"
                  src={heromob}
                  alt="App Interface"
                  sx={{
                    width: '320px',
                    maxWidth: '90vw',
                    height: 'auto',
                  }}
                />
              </Box>

              <Grid container spacing={4} justifyContent="center">
                {features.slice(2).map((feature, i) => (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    key={i + 2}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <Box textAlign="center" color="white" maxWidth={280}>
                      <Box mb={2}>{feature.icon}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: { lg: '58%', xl: '60%' },
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 2,
                  width: '100%',
                  maxWidth: { lg: '350px', xl: '400px' },
                  height: 'auto',
                }}
              >
                <Box
                  component="img"
                  src={heromob}
                  alt="App Interface"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: { lg: '500px', xl: '550px' },
                  }}
                />
              </Box>

              {/* Positioned features */}
              {/* Top */}
              <Box
                sx={{
                  position: 'absolute',
                  top: { lg: '2%', xl: '3%' },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 3,
                  textAlign: 'center',
                  width: 240,
                  color: 'white',
                }}
              >
                <Box sx={{ mb: 1.5, display: 'inline-block' }}>{features[0].icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {features[0].title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {features[0].description}
                </Typography>
              </Box>

              {/* Left */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: { lg: '5%', xl: '8%' },
                  zIndex: 3,
                  textAlign: 'center',
                  width: 240,
                  color: 'white',
                }}
              >
                <Box sx={{ mb: 1.5, display: 'inline-block' }}>{features[1].icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {features[1].title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {features[1].description}
                </Typography>
              </Box>

              {/* Right */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  right: { lg: '5%', xl: '8%' },
                  zIndex: 3,
                  textAlign: 'center',
                  width: 240,
                  color: 'white',
                }}
              >
                <Box sx={{ mb: 1.5, display: 'inline-block' }}>{features[2].icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {features[2].title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {features[2].description}
                </Typography>
              </Box>

              {/* Bottom Left */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { lg: '12%', xl: '14%' },
                  left: { lg: '20%', xl: '22%' },
                  zIndex: 3,
                  textAlign: 'center',
                  width: 200,
                  color: 'white',
                }}
              >
                <Box sx={{ mb: 1.5, display: 'inline-block' }}>{features[3].icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {features[3].title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {features[3].description}
                </Typography>
              </Box>

              {/* Bottom Right */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { lg: '12%', xl: '14%' },
                  right: { lg: '20%', xl: '22%' },
                  zIndex: 3,
                  textAlign: 'center',
                  width: 200,
                  color: 'white',
                }}
              >
                <Box sx={{ mb: 1.5, display: 'inline-block' }}>{features[4].icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {features[4].title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {features[4].description}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default AppFeaturesSection;
